import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { AlertTriangle, HelpCircle, ShieldAlert } from 'lucide-react';

export interface DialogOptions {
  title?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'info' | 'warning' | 'danger';
  placeholder?: string;
  inputType?: string;
}

interface DialogContextProps {
  confirm: (message: string, options?: DialogOptions) => Promise<boolean>;
  prompt: (message: string, defaultValue?: string, options?: DialogOptions) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextProps | null>(null);

interface DialogState {
  isOpen: boolean;
  message: string;
  options: DialogOptions;
  mode: 'confirm' | 'prompt';
  defaultValue: string;
  inputValue: string;
  resolve: (value: any) => void;
}

export function CustomDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const confirm = useCallback((message: string, options?: DialogOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({
        isOpen: true,
        message,
        options: options || {},
        mode: 'confirm',
        defaultValue: '',
        inputValue: '',
        resolve,
      });
    });
  }, []);

  const prompt = useCallback((message: string, defaultValue: string = '', options?: DialogOptions) => {
    return new Promise<string | null>((resolve) => {
      setState({
        isOpen: true,
        message,
        options: options || {},
        mode: 'prompt',
        defaultValue,
        inputValue: defaultValue,
        resolve,
      });
    });
  }, []);

  const handleClose = useCallback((value: any) => {
    if (state) {
      state.resolve(value);
      setState(null);
    }
  }, [state]);

  // Handle keyboard events (Escape to close, Enter to submit for prompt)
  useEffect(() => {
    if (!state?.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose(state.mode === 'confirm' ? false : null);
      } else if (e.key === 'Enter' && state.mode === 'prompt') {
        e.preventDefault();
        handleClose(state.inputValue);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state, handleClose]);

  // Autofocus the input when prompt opens
  useEffect(() => {
    if (state?.isOpen && state.mode === 'prompt') {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    }
  }, [state?.isOpen, state?.mode]);

  // Get the appropriate icon and accent color based on dialog type
  const getIconAndColors = () => {
    const type = state?.options.type || 'info';
    if (type === 'danger') {
      return {
        icon: <ShieldAlert size={24} color="var(--danger)" />,
        bg: 'rgba(186, 26, 26, 0.1)',
        btnClass: 'btn btn--danger',
      };
    }
    if (type === 'warning') {
      return {
        icon: <AlertTriangle size={24} color="var(--warning)" />,
        bg: 'rgba(255, 209, 102, 0.1)',
        btnClass: 'btn btn--primary',
      };
    }
    return {
      icon: <HelpCircle size={24} color="var(--accent)" />,
      bg: 'var(--accent-subtle)',
      btnClass: 'btn btn--primary',
    };
  };

  const { icon, bg, btnClass } = getIconAndColors();

  return (
    <DialogContext.Provider value={{ confirm, prompt }}>
      {children}
      {state?.isOpen && (
        <div
          className="modal-backdrop"
          onClick={() => handleClose(state.mode === 'confirm' ? false : null)}
          style={{
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            className="card animate-scale-in"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '400px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              padding: 'var(--space-lg)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-md)',
            }}
          >
            {/* Header with icon and title */}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--radius-md)',
                  background: bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {icon}
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <h3 className="text-1 font-700 text-md" style={{ margin: 0, lineHeight: 1.2 }}>
                  {state.options.title || (state.mode === 'confirm' ? 'Confirm Action' : 'Input Required')}
                </h3>
                <p className="text-2 text-sm" style={{ margin: 0, lineHeight: 1.4 }}>
                  {state.message}
                </p>
              </div>
            </div>

            {/* Input field for prompt mode */}
            {state.mode === 'prompt' && (
              <div className="input-group" style={{ margin: '4px 0 0' }}>
                <input
                  ref={inputRef}
                  type={state.options.inputType || 'text'}
                  className="input font-mono"
                  style={{ width: '100%', textAlign: 'center', letterSpacing: state.options.inputType === 'password' ? '6px' : 'normal' }}
                  value={state.inputValue}
                  onChange={(e) => setState((prev) => prev ? { ...prev, inputValue: e.target.value } : null)}
                  placeholder={state.options.placeholder || ''}
                />
              </div>
            )}

            {/* Actions buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
              <button
                className="btn btn--ghost"
                onClick={() => handleClose(state.mode === 'confirm' ? false : null)}
                style={{ padding: '10px 16px', fontSize: 'var(--text-sm)' }}
              >
                {state.options.cancelText || 'Cancel'}
              </button>
              <button
                className={btnClass}
                onClick={() => handleClose(state.mode === 'confirm' ? true : state.inputValue)}
                style={{ padding: '10px 20px', fontSize: 'var(--text-sm)' }}
              >
                {state.options.confirmText || (state.mode === 'confirm' ? 'Confirm' : 'Submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}

export function useDialog(): DialogContextProps {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within <CustomDialogProvider>');
  return ctx;
}
